/**
 * Intentsify brand tokens. Colour values come from the brand guide
 * (https://live.standards.site/intentsify/color); guide names are noted next
 * to each value. The 50–900 scales below are anchored on the guide's shades
 * (tint / 100 / base / 300) and interpolated in between for UI libraries.
 */
export const palette = {
    /** intentsify.io dark sections. */
    night: "#111514",
    /** Charcoal. */
    ink: "#1B1E1D",
    inkSecondary: "#212121",
    muted: "#6C7376",
    /** Anti-Flash White. */
    border: "#EBEDEF",
    /** Seasalt. */
    surface: "#F8FAFB",
    white: "#FFFFFF",
    /**
     * Seafoam 300. Buttons, links and the primary chart series in light mode:
     * white text on it passes WCAG AA (4.7:1), Seafoam 200 does not (3.1:1).
     */
    green: "#1C8267",
    /** Seafoam 200. Brand fills, gradients; primary on dark surfaces. */
    mint: "#20A482",
    /** Seafoam 100. */
    seafoamLight: "#5FDDBC",
    /** Green. */
    lime: "#6EB21F",
    /** Teal 100. */
    blue: "#3DABD2",
    /** Teal. */
    teal: "#075A77",
    /** Pink. */
    magenta: "#CD3372",
    /** Orange. */
    orange: "#E57F23",
    /** Yellow. */
    gold: "#D8B408",
    /** Blue Violet. */
    violet: "#8630D1",
    /** Legacy portal navy, kept as a secondary colour in the guide. */
    navy: "#031326",
    red: "#D64545",
};
/**
 * Stops are [offset 0–1, colour]. The brand guide's approved gradients; use
 * sparingly. Avoid mixing hues from opposite sides of the colour wheel (blue
 * to orange, green to pink, pink to yellow, seafoam to pink).
 */
export const gradientStops = {
    /** Thin bar under the header: Pink to Orange, then the Gold gradient. */
    bar: [
        [0, palette.magenta],
        [0.55, palette.orange],
        [1, palette.gold],
    ],
    /** Decorative "pipe" shapes: Blue to Pink. */
    accent: [
        [0, palette.blue],
        [1, palette.magenta],
    ],
    /** Primary call-to-action buttons: Seafoam to Green. */
    cta: [
        [0, palette.mint],
        [1, palette.lime],
    ],
    blue: [
        [0, palette.teal],
        [1, palette.blue],
    ],
    green: [
        [0, "#3F690E"],
        [1, palette.lime],
    ],
    gold: [
        [0, palette.gold],
        [1, palette.orange],
    ],
    pink: [
        [0, palette.magenta],
        [1, "#661A39"],
    ],
    seafoamToYellow: [
        [0, palette.mint],
        [1, palette.gold],
    ],
    pinkToOrange: [
        [0, palette.magenta],
        [1, palette.orange],
    ],
    seafoamToPurple: [
        [0, palette.mint],
        [1, "#9649FF"],
    ],
};
export const linearGradient = (stops, angle = "90deg") => `linear-gradient(${angle}, ${stops
    .map(([offset, color]) => `${color} ${offset * 100}%`)
    .join(", ")})`;
export const fonts = {
    family: "Outfit",
    stack: "Outfit, system-ui, -apple-system, 'Segoe UI', sans-serif",
    weights: [400, 500, 600, 700],
};
/** Warm neutral ramp: white page → #F4F4F4 surface → ink. */
/** Cool neutral ramp: Seasalt (25) → Anti-Flash White (100) → Charcoal (900). */
export const neutral = {
    25: palette.surface,
    50: "#F2F4F5",
    100: palette.border,
    200: "#D3D7DA",
    300: "#B3B9BD",
    400: "#8F969A",
    500: palette.muted,
    600: "#555B5E",
    700: "#3E4345",
    750: "#333739",
    800: "#2A2E2F",
    900: palette.ink,
};
/**
 * Dark surfaces, lightest (50) to darkest (900). 375–450 are the dark-mode
 * page, header and sidebar backgrounds; 300 is a raised card.
 */
export const night = {
    50: "#7E8784",
    100: "#5C6461",
    200: "#3B423F",
    300: "#232927",
    375: "#191E1C",
    400: "#161B19",
    425: "#141917",
    450: "#111514",
    500: "#0E1211",
    600: "#0C0F0E",
    700: "#0A0D0C",
    800: "#080A0A",
    900: "#060807",
};
/** Brand green; 500 = mint, 600 = primary green. */
/** Seafoam: 50 Azure, 300 Seafoam 100, 500 Seafoam 200, 600 Seafoam 300. */
export const green = {
    50: "#F0FFFB",
    100: "#CFF5EA",
    200: "#9FEBD6",
    300: palette.seafoamLight,
    400: "#3CC09E",
    500: palette.mint,
    600: palette.green,
    700: "#176B55",
    800: "#125443",
    900: "#0D3D31",
};
/** Green: 50 tint, 300 Green 100, 500 Green, 800 Green 300. */
export const lime = {
    50: "#EEFFDA",
    100: "#D8F8B2",
    200: "#C1F288",
    300: "#AAED5C",
    400: "#8CD03D",
    500: palette.lime,
    600: "#5C9A1A",
    700: "#4E8214",
    800: "#3F690E",
    900: "#2E4D0A",
};
/** Teal: 50 tint, 500 Teal 100, 700 Teal, 900 Teal 300. */
export const blue = {
    50: "#E3F8FF",
    100: "#BDEBFA",
    200: "#8FD6EE",
    300: "#64C1E0",
    400: "#4DB6D9",
    500: palette.blue,
    600: "#2183A6",
    700: palette.teal,
    800: "#04475F",
    900: "#003548",
};
/** Pink: 50 tint, 300 Pink 100, 500 Pink, 800 Pink 300. */
export const magenta = {
    50: "#FFEFF5",
    100: "#FDD3E3",
    200: "#FBA9C9",
    300: "#F876AB",
    400: "#E2548E",
    500: palette.magenta,
    600: "#A82A5E",
    700: "#85224B",
    800: "#661A39",
    900: "#4A1229",
};
/** Yellow: 50 tint, 300 Yellow 100, 500 Yellow, 700 Yellow 300. */
export const gold = {
    50: "#FFFBEA",
    100: "#FDF1BC",
    200: "#FBE68A",
    300: "#F8DB49",
    400: "#E9C726",
    500: palette.gold,
    600: "#B79905",
    700: "#967D00",
    800: "#756100",
    900: "#544600",
};
export const orange = {
    50: "#FDF0E4",
    100: "#F9D9BC",
    200: "#F4BE8C",
    300: "#EFA35C",
    400: "#EA913F",
    500: palette.orange,
    600: "#C26A1C",
    700: "#9E5617",
    800: "#7A4211",
    900: "#572F0C",
};
/** Blue Violet (secondary). */
export const violet = {
    50: "#F3EAFB",
    100: "#E1CBF5",
    200: "#C9A0EC",
    300: "#B075E2",
    400: "#9B52DA",
    500: palette.violet,
    600: "#7027B0",
    700: "#5A1F8E",
    800: "#44176B",
    900: "#2E1049",
};
export const red = {
    50: "#FBE7E7",
    100: "#F5C3C3",
    200: "#EE9A9A",
    300: "#E77272",
    400: "#DE5A5A",
    500: palette.red,
    600: "#B93A3A",
    700: "#992F2F",
    800: "#782525",
    900: "#581B1B",
};
/** Buyer research stages, in funnel order. */
export const buyerResearchStageColors = {
    Awareness: palette.magenta,
    Interest: palette.blue,
    Consideration: palette.gold,
    Decision: palette.green,
};
/** Page-level colours per colour mode. */
export const modes = {
    light: {
        page: palette.white,
        surface: palette.surface,
        raised: palette.white,
        border: palette.border,
        text: palette.ink,
        muted: palette.muted,
        primary: palette.green,
        onPrimary: palette.white,
    },
    dark: {
        page: night[375],
        surface: night[300],
        raised: night[200],
        border: "#2C3230",
        text: palette.surface,
        muted: "#A3ABA8",
        primary: palette.mint,
        onPrimary: palette.night,
    },
};
/**
 * Categorical series order follows the guide's colour proportion: Seafoam
 * first, then Teal, Yellow, Pink and Green, then secondary colours for long
 * breakdowns (e.g. top 10 industries).
 */
export const seriesPalette = {
    light: [
        palette.green,
        palette.blue,
        palette.gold,
        palette.magenta,
        palette.lime,
        palette.teal,
        palette.violet,
        palette.orange,
        palette.seafoamLight,
        magenta[800],
    ],
    dark: [
        palette.mint,
        palette.blue,
        palette.gold,
        magenta[300],
        palette.lime,
        blue[200],
        violet[300],
        palette.orange,
        green[100],
        magenta[200],
    ],
};
/** Neutral used for catch-all categories such as "Other". */
export const otherSeriesColor = {
    light: palette.ink,
    dark: neutral[300],
};
/** Chart chrome (text, guides, tooltips) per colour mode. */
export const chartTheme = {
    light: {
        foreground: palette.ink,
        muted: palette.muted,
        grid: "#E6E9EB",
        tooltipBackground: palette.white,
        tooltipBorder: palette.border,
    },
    dark: {
        foreground: palette.surface,
        muted: modes.dark.muted,
        grid: "#2C3230",
        tooltipBackground: night[300],
        tooltipBorder: "#2C3230",
    },
};
export const radii = {
    sm: "6px",
    md: "10px",
    lg: "16px",
    xl: "24px",
};
//# sourceMappingURL=index.js.map